import React from 'react';
import gql from 'graphql-tag';
import { P3Configuration, ProgramHeading } from 'models/interfaces/P3Configurations';
import { Query } from 'react-apollo';
import { ThomasCampaign, ThomasAdvertiser, ThomasUserInfo } from 'models/interfaces';
import {
  ConfigurationPoint,
  ConfigurationBarPoint
} from 'models/interfaces/configurationExport/export';
import {
  getNationalPoints,
  getRegionalPoints,
  getNationalTable,
  getRegionalTable,
  getPointsData,
  getPoints
} from 'util/p3ConfigurationExport';

const CONFIGURATION_EXPORT_CAMPAIGN = gql`
  query ConfigurationExportCampaign(
    $campaign_id: String!
    $tgrams_id: String!
    $configuration_id: String!
  ) {
    configuration: p3Configuration(p3_configuration_id: $configuration_id) {
      p3_configuration_id
      p3_campaign_id
      configuration_status_id
      description
      name
      tgrams_id
      companyName
      campaign_status_id
      rank_points
      coverage_areas
      preview_ads
      program_value
      last_updated_date
      last_updated_by
      created_date
      start_date
      end_date
      created_by
      regions {
        p3_configuration_id
        region_code
        name
        last_updated_by
        last_updated_date
        created_date
        created_by
      }
      categories {
        p3_configuration_id
        category_id
        category_name
        national_purchased_rank_points
        national_earned_rank_points
        national_bonus_rank_points
        national_total_rank_points
        national_estimated_rank
        regional_purchased_rank_points
        regional_earned_rank_points
        regional_bonus_rank_points
        regional_total_rank_points
        regional_estimated_ranks
        has_national_preview_ad
        has_regional_preview_ad
        last_updated_by
        last_updated_date
        created_by
      }
    }
    program(configuration_id: $configuration_id) {
      headingId
      region {
        name
        position
      }
    }
    campaign: thomasCampaign(campaign_id: $campaign_id) {
      campaign_id
      tgrams_id
      product_id
      name
      start_date
      end_date
      status_id
      created_date
      last_updated_by
      last_updated_date
    }
    account: thomasAdvertiserSimple(tgrams_id: $tgrams_id) {
      name
      tgrams_id
      status_id
    }
    user: UserInfo {
      firstName
      lastName
      email
    }
  }
`;

const CONFIGURATION_EXPORT_TGRAMS = gql`
  query ConfigurationExportTgrams($tgrams_id: String!, $configuration_id: String!) {
    configuration: p3Configuration(p3_configuration_id: $configuration_id) {
      p3_configuration_id
      p3_campaign_id
      configuration_status_id
      description
      name
      tgrams_id
      companyName
      campaign_status_id
      rank_points
      coverage_areas
      preview_ads
      program_value
      last_updated_date
      last_updated_by
      created_date
      start_date
      end_date
      created_by
      regions {
        p3_configuration_id
        region_code
        name
        last_updated_by
        last_updated_date
        created_date
        created_by
      }
      categories {
        p3_configuration_id
        category_id
        category_name
        national_purchased_rank_points
        national_earned_rank_points
        national_bonus_rank_points
        national_total_rank_points
        national_estimated_rank
        regional_purchased_rank_points
        regional_earned_rank_points
        regional_bonus_rank_points
        regional_total_rank_points
        regional_estimated_ranks
        has_national_preview_ad
        has_regional_preview_ad
        last_updated_by
        last_updated_date
        created_by
      }
    }
    program(configuration_id: $configuration_id) {
      headingId
      region {
        name
        position
      }
    }
    account: thomasAdvertiserSimple(tgrams_id: $tgrams_id) {
      name
      tgrams_id
      status_id
    }
    user: UserInfo {
      firstName
      lastName
      email
    }
  }
`;

const CONFIGURATION_EXPORT = gql`
  query ConfigurationExport($configuration_id: String!) {
    configuration: p3Configuration(p3_configuration_id: $configuration_id) {
      p3_configuration_id
      p3_campaign_id
      configuration_status_id
      description
      name
      tgrams_id
      companyName
      campaign_status_id
      rank_points
      coverage_areas
      preview_ads
      program_value
      last_updated_date
      last_updated_by
      created_date
      start_date
      end_date
      created_by
      regions {
        p3_configuration_id
        region_code
        name
        last_updated_by
        last_updated_date
        created_date
        created_by
      }
      categories {
        p3_configuration_id
        category_id
        category_name
        national_purchased_rank_points
        national_earned_rank_points
        national_bonus_rank_points
        national_total_rank_points
        national_estimated_rank
        regional_purchased_rank_points
        regional_earned_rank_points
        regional_bonus_rank_points
        regional_total_rank_points
        regional_estimated_ranks
        has_national_preview_ad
        has_regional_preview_ad
        last_updated_by
        last_updated_date
        created_by
      }
    }
    program(configuration_id: $configuration_id) {
      headingId
      region {
        name
        position
      }
    }
    user: UserInfo {
      firstName
      lastName
      email
    }
  }
`;

interface P3ConfigurationsExportContainerRenderProps {
  nationalPointsData?: ConfigurationPoint[];
  nationalTableData?: ConfigurationPoint[];
  regionalPointsData?: ConfigurationPoint[];
  regionalTableData?: ConfigurationPoint[];
  pointsData?: ConfigurationBarPoint[];
  configuration: P3Configuration;
  account?: ThomasAdvertiser;
  campaign?: ThomasCampaign;
  user: ThomasUserInfo;
}

interface P3ConfigurationsExportContainerProps {
  tgrams_id: string;
  campaign_id: string;
  configuration_id: string;
  children: (props: P3ConfigurationsExportContainerRenderProps) => any;
}

interface P3ConfigurationsQueryResults {
  configuration: P3Configuration;
  campaign: ThomasCampaign;
  account: ThomasAdvertiser;
  program: ProgramHeading[];
  user: any;
}

//class ConfigurationsQuery extends Query<P3ConfigurationsQueryResults> {}

const P3ConfigurationsExportContainer: React.FunctionComponent<
  P3ConfigurationsExportContainerProps
> = ({ children, tgrams_id, campaign_id, configuration_id, ...props }) => {
  let query;
  let variables;

  if (tgrams_id !== '' && campaign_id !== '' && configuration_id !== '') {
    query = CONFIGURATION_EXPORT_CAMPAIGN;
    variables = { tgrams_id, campaign_id, configuration_id };
  } else if (configuration_id !== '' && tgrams_id !== '') {
    query = CONFIGURATION_EXPORT_TGRAMS;
    variables = { configuration_id, tgrams_id };
  } else if (configuration_id !== '') {
    query = CONFIGURATION_EXPORT;
    variables = { configuration_id };
  }

  return (
    <Query<P3ConfigurationsQueryResults>
      query={query}
      variables={variables}
      fetchPolicy="cache-and-network"
    >
      {({
        data: { configuration, campaign, account, program, user } = {
          configuration: null,
          campaign: undefined,
          account: undefined,
          program: null,
          user: undefined
        },
        loading
      }) => {
        if (loading) {
          return null;
        }

        if (configuration === null) {
          configuration = {} as P3Configuration;
        }
        if (program === null) {
          program = [] as ProgramHeading[];
        }
        let nationalTableData = getPoints(configuration.categories, true, program);
        let regionalTableData = getPoints(configuration.categories, false, program);
        let nationalPointsData = getNationalPoints(nationalTableData);
        let regionalPointsData = getRegionalPoints(regionalTableData); 

        let pointsData = getPointsData(configuration);

        return (
          children &&
          children({
            ...props,
            account,
            campaign,
            user,
            configuration,
            pointsData,
            nationalPointsData,
            nationalTableData,
            regionalPointsData,
            regionalTableData
          })
        );
      }}
    </Query>
  );
};

export default P3ConfigurationsExportContainer;
